import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMediaQuery, useScrollDirection, useWindowSize } from './index'
import { twoLevelsAnchorType } from '../types'
import style from '../_mixins.scss'

export const getFirstAnchor = (anchorsList?: twoLevelsAnchorType[]) => {
    return anchorsList ? anchorsList[0]?.anchor ?? '' : ''
}

type scrollPositionsType = {
    up: number[]
    down: number[]
}

type activeAnchorProp = {
    anchors?: twoLevelsAnchorType[]
    scrollAtRender?: boolean
}

export const useActiveAnchor = ({ anchors, scrollAtRender = false }: activeAnchorProp) => {
    const [activeAnchor, setActiveAnchor] = useState(window.location.hash)
    const [mapAnchors, setMapAnchors] = useState({}) // (key, value) = (position, anchor)
    const [scrollPositions, setScrollPositions] = useState<scrollPositionsType>({
        up: [],
        down: [],
    })

    const scrollDir = useScrollDirection({ initialDirection: 'up', thresholdPixels: 0 })
    const windowSize = useWindowSize()
    const isTablet = useMediaQuery('(max-width: ' + style.tabletBreakpoint + ')')
    const isMobile = useMediaQuery('(max-width: ' + style.mobileBreakpoint + ')')

    const firstAnchor = useMemo(() => getFirstAnchor(anchors), [anchors])
    const stickyHeaderHeight = useMemo(
        () => (isMobile ? 90 : isTablet ? 95 : 0),
        [isMobile, isTablet],
    )

    /**
     * Calculate all anchors positions.
     */
    const calculateAnchorPositions = useCallback(() => {
        if (!anchors) {
            return
        }

        const data: scrollPositionsType = {
            up: [],
            down: [],
        }
        anchors?.forEach((a) => {
            if (a?.anchor) {
                const element = document.getElementById(a.anchor.replace('#', ''))
                if (element) {
                    const position = element.offsetTop
                    data.up.push(position - stickyHeaderHeight)
                    data.down.push(position)
                    setMapAnchors((prev) => {
                        return {
                            ...prev,
                            [position]: a.anchor,
                            [position - stickyHeaderHeight]: a.anchor,
                        }
                    })
                }
            }
            if (a?.subAnchors) {
                a.subAnchors.forEach((sa) => {
                    if (sa?.anchor) {
                        const element = document.getElementById(sa.anchor.replace('#', ''))
                        if (element) {
                            const position = element.offsetTop
                            data.up.push(position - stickyHeaderHeight)
                            data.down.push(position)
                            setMapAnchors((prev) => {
                                return {
                                    ...prev,
                                    [position]: sa.anchor,
                                    [position - stickyHeaderHeight]: sa.anchor,
                                }
                            })
                        }
                    }
                })
            }
        })
        // Sort in descending order
        data.up.sort((a, b) => b - a)
        data.down.sort((a, b) => b - a)
        setScrollPositions(data)
    }, [anchors, stickyHeaderHeight])

    /**
     * Get the position of an element taking into account the size of the header.
     */
    const getElementPosition = (element: HTMLElement, headerHeight: number) => {
        let position = element.offsetTop
        const rect = element.getBoundingClientRect()
        if (rect.top < 0) {
            position -= headerHeight
        }
        return position
    }

    /**
     * Scroll in JS to an anchor position. It is used to compensate the height
     * of the sticky header.
     */
    const scrollToAnchor = useCallback(
        (anchor: string) => {
            // Scroll to element position according to user position in the window
            let element = document.getElementById(anchor.replace('#', ''))
            if (element) {
                window.scrollTo({
                    top: getElementPosition(element, stickyHeaderHeight),
                    behavior: 'smooth',
                })
            }
            // Behave like a link by changing the URL
            const href = anchor.includes('#') ? anchor : `#${anchor}`
            window.history.pushState(
                {},
                '',
                `${window.location.pathname}${window.location.search}${href}`,
            )
        },
        [stickyHeaderHeight],
    )

    /**
     * Set the active anchor according to window position.
     */
    const handleActivateAnchorByPosition = useCallback(() => {
        scrollPositions[scrollDir].every((position, index) => {
            // Scroll is under an anchor position
            if (position <= window.scrollY) {
                setActiveAnchor((old) =>
                    old !== mapAnchors[position] ? mapAnchors[position] : old,
                )
                return false
            }
            // Scroll is above all anchors
            if (index === scrollPositions[scrollDir].length - 1) {
                setActiveAnchor((old) => (old !== firstAnchor ? firstAnchor : old))
            }
            return true
        })
    }, [scrollPositions, scrollDir, mapAnchors, firstAnchor])

    /**
     * Get all anchors positions when window size changes
     */
    useEffect(() => {
        // Timeout is used because the calculated positions after rendering
        // are not correct. This can be explain because of dynamic content
        // and window resizing.
        const timer = setTimeout(calculateAnchorPositions, 100)
        return () => clearTimeout(timer)
    }, [calculateAnchorPositions, windowSize.width, windowSize.height])

    /**
     * Get all anchors positions at first render
     * Calculate anchors positions each 3s, before page is fully loaded.
     */
    useEffect(() => {
        const interval = setInterval(() => {
            calculateAnchorPositions()
        }, 3000)

        const onLoadAnchorPositions = () => {
            calculateAnchorPositions()
            clearInterval(interval)
        }
        window.addEventListener('load', onLoadAnchorPositions)
        return () => {
            window.removeEventListener('load', onLoadAnchorPositions)
        }
    }, [calculateAnchorPositions])

    /**
     * Add event listener to set active anchor when scroll pass under an anchor.
     */
    useEffect(() => {
        document.addEventListener('scroll', handleActivateAnchorByPosition)
        return () => {
            document.removeEventListener('scroll', handleActivateAnchorByPosition)
        }
    }, [handleActivateAnchorByPosition])

    /**
     * At first render, activate the anchor according to the window position.
     */
    useEffect(() => {
        if (!window.location.hash) {
            handleActivateAnchorByPosition()
        }
    }, [handleActivateAnchorByPosition])

    /**
     * At first render, scroll to anchor in URL
     */
    useEffect(() => {
        if (!scrollAtRender || !window.location.hash) {
            return
        }
        // Timeout is used because the calculated positions after rendering
        // are not correct. This can be explain because of dynamic content
        // and window resizing.
        const timer = setTimeout(() => {
            scrollToAnchor(window.location.hash)
        }, 100)
        return () => clearTimeout(timer)
        // eslint-disable-next-line
    }, [])

    return {
        activeAnchor,
        setActiveAnchor,
        scrollToAnchor,
    }
}
